'use client';

/**
 * ComposerQuickChatCard — pre-submit vendor nudge.
 *
 * Sits in the Composer rail stack between brief and submit. Reads
 * recipients from `useCartStore.vendorIds` (cart-scoped, NOT
 * selection-scoped) — different entry point from the chat surface's
 * `compose_message` card, which is selection-scoped. Use case:
 * "ping vendor about capacity *before* I formalize this RFQ".
 *
 * Phone resolution is at send time (per-vendor `vendor_details`
 * fetch in parallel), not at mount — avoids N hooks-at-render and
 * keeps the card's render path lean for the typical "draft, never
 * send" case.
 *
 * Hides on `mcp` / `rfq_edit` intents (pre-submit nudges don't
 * apply to cost plans or in-flight RFQ edits) and when the cart's
 * vendor list is empty.
 *
 * Capability-resolved strategies — `(vendor_intelligence, detail)`
 * for phone resolution, `(messaging, send_whatsapp)` for delivery.
 *
 * @see klyde_edge/composition/cards/composer.py — composer_quick_chat spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useState } from 'react';
import { MessageSquare, Loader2, Phone, CheckCircle, AlertTriangle } from 'lucide-react';
import { useCartStore } from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useComposerLabel, useTabLabel } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';

interface VendorDetail {
  phone: string | null;
  odoo_id: number | null;
  name?: string | null;
}

interface SendResult {
  success: number;
  failed: number;
  noPhone: number;
}

export function ComposerQuickChatCard() {
  // i18n labels — server-driven, lang follows the matrix fetch.
  // Templated labels (with `{count}`-style placeholders) are called
  // inline at the JSX site so the substitution sees the current values.
  const cardTitle = useTabLabel('Quick Message');
  const labelTextareaPlaceholder = useComposerLabel('quick_textarea_placeholder');
  const labelIncludeSummary = useComposerLabel('quick_include_summary');
  const labelSendWhatsApp = useComposerLabel('quick_send_whatsapp');
  const labelSending = useComposerLabel('quick_sending');
  const labelStrategiesMissing = useComposerLabel('strategies_missing');

  const activeIntent = useCartStore((s) => s.activeIntent);
  const items = useCartStore((s) => s.items);
  const vendorIds = useCartStore((s) => s.vendorIds);
  const vendorNames = useCartStore((s) => s.vendorNames);
  const projectName = useCartStore((s) => s.projectName);
  const requestedDate = useCartStore((s) => s.requestedDate);

  const vendorDetailsStrategy = useCapabilityStore((s) =>
    s.strategyName('vendor_intelligence', 'detail'),
  );
  const sendWhatsAppStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'send_whatsapp'),
  );
  const selectedUser = useDevUserStore((s) => s.selectedUser);

  const [message, setMessage] = useState('');
  const [includeSummary, setIncludeSummary] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pre-submit nudges don't apply to cost plans (no vendor delivery
  // step) or to in-flight RFQ edits (vendors are already in
  // conversation via the chat surface). Cart vendor list empty →
  // nobody to message.
  if (activeIntent === 'mcp' || activeIntent === 'rfq_edit') return null;
  if (vendorIds.length === 0) return null;

  const selectedIds = vendorIds.filter((id) => !excluded.has(id));
  const canSend = selectedIds.length > 0 && message.trim().length > 0 && !isSending;

  const buildBody = () => {
    if (!includeSummary) return message;
    const parts = [`${items.length} item${items.length === 1 ? '' : 's'}`];
    if (projectName) parts.push(`for ${projectName}`);
    if (requestedDate) parts.push(`delivery ${requestedDate}`);
    return `${message}\n\n— ${parts.join(' · ')}`;
  };

  const toggleVendor = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcluded(next);
  };

  const handleSend = async () => {
    if (!canSend) return;
    if (!vendorDetailsStrategy || !sendWhatsAppStrategy) {
      setError(labelStrategiesMissing);
      return;
    }
    setIsSending(true);
    setResult(null);
    setError(null);

    const body = buildBody();
    const tally: SendResult = { success: 0, failed: 0, noPhone: 0 };

    // Resolve phones in parallel, then dispatch sends serially —
    // simple and avoids hammering the messaging gateway.
    const detailsResults = await Promise.all(
      selectedIds.map((id) =>
        api
          .getStrategyData<VendorDetail>(vendorDetailsStrategy, { semantic_id: id })
          .then((r) => ({ id, ok: r.success, data: r.data ?? null }))
          .catch(() => ({ id, ok: false, data: null as VendorDetail | null })),
      ),
    );

    for (const r of detailsResults) {
      if (!r.ok || !r.data?.phone) {
        tally.noPhone += 1;
        continue;
      }
      try {
        const sendRes = await api.getStrategyData(sendWhatsAppStrategy, {
          to: r.data.phone,
          message: body,
          context_model: 'res.partner',
          context_id: r.data.odoo_id,
          author_name: selectedUser?.name,
        });
        if (sendRes.success) tally.success += 1;
        else tally.failed += 1;
      } catch {
        tally.failed += 1;
      }
    }

    setIsSending(false);
    setResult(tally);
    if (tally.success > 0 && tally.failed === 0 && tally.noPhone === 0) {
      setMessage('');
    }
  };

  return (
    <RailCard title={cardTitle} icon={MessageSquare}>
      <div className="mb-2">
        <label className={cn(text.muted, 'block mb-1')}>
          {useComposerLabel('quick_to_n_of_m', { selected: selectedIds.length, total: vendorIds.length })}
        </label>
        <div className="flex flex-wrap gap-1">
          {vendorIds.map((id, idx) => {
            const active = !excluded.has(id);
            return (
              <button
                key={id}
                onClick={() => toggleVendor(id)}
                className={cn(
                  text.body,
                  'px-2 py-0.5 rounded transition-colors',
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground line-through opacity-60 hover:opacity-100',
                )}
              >
                {vendorNames[idx]}
              </button>
            );
          })}
        </div>
      </div>

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={labelTextareaPlaceholder}
        className={cn(
          text.body,
          'w-full h-20 px-2 py-1.5 rounded-md border border-border bg-background',
          'placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary',
        )}
      />

      <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
        <input
          type="checkbox"
          checked={includeSummary}
          onChange={(e) => setIncludeSummary(e.target.checked)}
          className="h-3 w-3"
        />
        <span className={text.muted}>{labelIncludeSummary}</span>
      </label>

      <button
        onClick={handleSend}
        disabled={!canSend}
        className={cn(
          text.body,
          'mt-2 w-full font-medium px-3 py-1.5 rounded-md flex items-center justify-center gap-2 transition-colors',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {isSending ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {labelSending}
          </>
        ) : (
          <>
            <Phone className="h-3.5 w-3.5" />
            {labelSendWhatsApp}
          </>
        )}
      </button>

      {error && (
        <div className="mt-2 rounded-md p-2 flex items-start gap-2 bg-destructive/10 text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p className={text.body}>{error}</p>
        </div>
      )}

      {result && !error && (
        <div
          className={cn(
            'mt-2 rounded-md p-2 flex items-start gap-2',
            result.failed === 0 && result.noPhone === 0
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
          )}
        >
          <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <ResultBody result={result} totalSelected={selectedIds.length} />
        </div>
      )}
    </RailCard>
  );
}

function ResultBody({
  result,
  totalSelected,
}: {
  result: SendResult;
  totalSelected: number;
}) {  const headline = useComposerLabel(
    totalSelected === 1 ? 'quick_sent_to_n_of_m_one' : 'quick_sent_to_n_of_m',
    { sent: result.success, total: totalSelected },
  );
  const missing = useComposerLabel('quick_n_missing_phone', { count: result.noPhone });
  const failed = useComposerLabel('quick_n_send_failed', { count: result.failed });
  return (
    <div className="min-w-0">
      <p className={text.body}>{headline}</p>
      {(result.failed > 0 || result.noPhone > 0) && (
        <p className={text.muted}>
          {result.noPhone > 0 && missing}
          {result.noPhone > 0 && result.failed > 0 && ' · '}
          {result.failed > 0 && failed}
        </p>
      )}
    </div>
  );
}
